import { ScrollableArea } from '@approvalmax/ui';
import { ConditionWrapper, Grid } from '@approvalmax/ui/src/components';
import { useAtomValue } from 'jotai';
import { constants } from 'modules/common';
import { domain } from 'modules/data';
import { changeQbooksMemo, updateQBooksFormWithContext } from 'pages/requestForm/actions';
import { FC, memo, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useUserBetaFeatures } from 'shared/hooks';
import { ocrIsLoadingState } from 'shared/states';
import styled from 'styled-components';

import { AttachmentsSection, MemoSection, RequesterInstructionSection } from '../../../components';
import FileDropArea from '../../../components/FileDropArea';
import FileDropProvider from '../../../components/FileDropProvider';
import { useUploadAttachments } from '../../../hooks/useUploadAttachments';
import { getContext } from '../../../selectors/pageSelectors';
import { getQBooksContext } from '../../../selectors/qbooks';
import ActionsBlock from '../../form/ActionsBlock/ActionsBlock';
import FilesSection from '../../form/FilesSection';
import { QBooksAccountLineItemsSection as QBooksAccountLineItemsSectionLegacy } from '../(legacy)/QBooksAccountLineItemsSection/QBooksAccountLineItemsSection';
import { QBooksFooterLegacy } from '../(legacy)/QBooksFooterLegacy/QBooksFooterLegacy';
import { QBooksAccountLineItemsSection } from '../QBooksAccountLineItemsSection/QBooksAccountLineItemsSection';
import { QBooksExpenseFieldsSection } from '../QBooksExpenseFieldsSection/QBooksExpenseFieldsSection';
import { QBooksLineItemsSection } from '../QBooksLineItemsSection/QBooksLineItemsSection';
import { QBooksRequestItemsSummary } from '../QBooksRequestItemsSummary/QBooksRequestItemsSummary';
import { messages } from './QBooksExpenseForm.messages';

const { qBooksConstants } = constants;

const Content = styled(ScrollableArea)`
    flex: 1;
    overflow: hidden;
`;

const ContentInner = styled.div`
    padding-bottom: 40px;
    display: flex;
    flex-flow: column;
`;

const Toolbar = styled.div`
    flex: none;
    padding: 25px 60px 30px;
    border-bottom: 1px solid #cacccd;
    display: flex;
    align-items: center;
`;

const ToolbarText = styled.div`
    flex: 1;
    font-weight: 600;
    font-size: 20px;
    color: #333;
    text-transform: uppercase;
`;

const Root = styled.div`
    display: flex;
    flex-flow: column;
    position: relative;
    height: 100%;
    width: 100%;
`;

interface QBooksExpenseFormProps {
    request: domain.QBooksExpenseRequest;
}

const QBooksExpenseForm: FC<QBooksExpenseFormProps> = (props) => {
    const { request } = props;

    const context = useSelector(getContext);
    const qBooksContext = useSelector(getQBooksContext);
    const ocrIsLoading = useAtomValue(ocrIsLoadingState);
    const dispatch = useDispatch();

    const qBooksEnabledLineItemType = qBooksContext.qbooksEnabledLineItemType;
    const isQBooksEnabledLineItemTypeAll =
        qBooksEnabledLineItemType === domain.TemplateSettingsQBooksEnabledLineItemType.All;
    const isAccountBasedLinesEnabled =
        isQBooksEnabledLineItemTypeAll ||
        qBooksEnabledLineItemType === domain.TemplateSettingsQBooksEnabledLineItemType.AccountBasedLines;
    const isItemBasedLinesEnabled =
        isQBooksEnabledLineItemTypeAll ||
        qBooksEnabledLineItemType === domain.TemplateSettingsQBooksEnabledLineItemType.ItemBasedLines;

    const { onUpload } = useUploadAttachments();

    useEffect(() => {
        dispatch(updateQBooksFormWithContext());
    }, [dispatch, context]);

    const {
        isBelgradeUIFilesUploadSection,
        isBelgradeUILineItemsTotalSection,
        isBelgradeUIQBooksAccountLineItemsSection,
    } = useUserBetaFeatures();

    const onChangeMemo = useCallback(
        (value: string) => {
            dispatch(changeQbooksMemo(value));
        },
        [dispatch]
    );

    return (
        <ConditionWrapper
            condition={!isBelgradeUIFilesUploadSection}
            wrapperIf={(children) => (
                <FileDropProvider
                    onUpload={onUpload}
                    maxSize={context.attachmentMaxSize}
                    allowedFileExtensions={qBooksConstants.QBO_ALLOWED_FILE_EXTENSIONS}
                >
                    {children}

                    <FileDropArea />
                </FileDropProvider>
            )}
        >
            <Root>
                <Toolbar>
                    <ToolbarText>{messages.toolbarText}</ToolbarText>

                    <ActionsBlock />
                </Toolbar>

                <Content>
                    <ContentInner>
                        <RequesterInstructionSection instructionText={context.requesterInstruction} />

                        <QBooksExpenseFieldsSection />

                        {isAccountBasedLinesEnabled &&
                            (isBelgradeUIQBooksAccountLineItemsSection ? (
                                <QBooksAccountLineItemsSection />
                            ) : (
                                <QBooksAccountLineItemsSectionLegacy />
                            ))}

                        {isItemBasedLinesEnabled && <QBooksLineItemsSection />}

                        {isBelgradeUILineItemsTotalSection ? <QBooksRequestItemsSummary /> : <QBooksFooterLegacy />}

                        <ConditionWrapper
                            condition={isBelgradeUIFilesUploadSection}
                            wrapperIf={(children) => (
                                <Grid gridTemplateColumns='1fr 1fr' gap={16}>
                                    {children}

                                    <AttachmentsSection
                                        request={request}
                                        extensions={qBooksConstants.qboAllowedFileExtensions}
                                    />
                                </Grid>
                            )}
                        >
                            <MemoSection
                                defaultValue={request.details?.memo || ''}
                                disabled={ocrIsLoading}
                                maxLength={constants.qBooksConstants.MEMO_MAX_LENGTH}
                                onChange={onChangeMemo}
                            />
                        </ConditionWrapper>

                        {!isBelgradeUIFilesUploadSection && <FilesSection />}
                    </ContentInner>
                </Content>
            </Root>
        </ConditionWrapper>
    );
};

export default memo(QBooksExpenseForm);
